import { Controller } from "@hotwired/stimulus"
import {
  loadFile,
  rebuildText,
  formatNumber,
  detectSaveKind,
} from "../lib/metroid_prime_origins_nut"
import {
  groupSlotFields,
  usesFieldGroups,
} from "../lib/metroid_prime_origins_field_groups"
import { EMPTY_SLOT_DOC, extractSlotStats } from "../lib/metroid_prime_origins_slot_stats"
import { DESCRIBED_SECTIONS, EDIT_TAB, render as renderReport, tabIds } from "../lib/metroid_prime_origins_report"
import { esc } from "../lib/game_tool/html"
import { readStorage, writeStorage } from "../lib/game_tool/storage"
import { nextTabId, tabId } from "../lib/game_tool/tabs"

const TAB_STORAGE = "mpo-tab"
const SPOILERS_STORAGE = "mpo-spoilers"
const descriptionsStorage = (section) => `mpo-descriptions-${section}`
const FILTER_STORAGE = {
  collection: "mpo-filter-collection",
  aeon: "mpo-filter-aeon",
  creatures: "mpo-filter-creatures",
  pirateData: "mpo-filter-pirateData",
  chozoLore: "mpo-filter-chozoLore",
  artifacts: "mpo-filter-artifacts",
}

// Metroid Prime: Origins save viewer/editor (client-side only — never uploads).
export default class extends Controller {
  static outlets = ["save-loader"]
  static values = { dataUrl: String }
  static targets = [
    "fusionPanel",
    "fusionContent",
    "report",
    "editHome",
    "browserPanel",
    "fieldsDetails",
    "fieldsRoot",
    "fieldTotal",
    "searchInput",
    "searchCount",
    "downloadBtn",
    "downloadJsonBtn",
  ]

  connect() {
    this.state = null
    this.stats = null
    this.inputRegistry = new Map()
    this.gameData = null
    this.ui = {
      tabs: { main: readStorage(TAB_STORAGE) },
      spoilers: readStorage(SPOILERS_STORAGE) === "1",
      descriptions: Object.fromEntries(DESCRIBED_SECTIONS.map((id) => [id, readStorage(descriptionsStorage(id)) !== "0"])),
      filters: Object.fromEntries(Object.entries(FILTER_STORAGE).map(([key, storageKey]) => [key, readStorage(storageKey) || "all"])),
    }
    this.loadGameData()
  }

  // Logbook text for the report. Saves load without it; the report just omits descriptions.
  async loadGameData() {
    try {
      const response = await fetch(this.dataUrlValue)
      if (!response.ok) return
      this.gameData = await response.json()
      if (this.stats) this.renderReport()
    } catch {}
  }

  // save-loader:reset ("Load a different file")
  reset() {
    this.state = null
    this.fusionPanelTarget.hidden = true
    this.hideReport()
    this.browserPanelTarget.hidden = true
    this.fieldsDetailsTarget.open = false
  }

  // save-loader:empty: a new game's save, view-only (no Edit tab), so spoilers decide what shows.
  onEmptySave() {
    this.reset()
    this.stats = { ...extractSlotStats(EMPTY_SLOT_DOC), empty: true }
    this.saveLoaderOutlet.element.hidden = true
    this.renderReport()
    this.reportTarget.scrollIntoView({ block: "start" })
  }

  // save-loader:loaded; the loader passes the File through (read-as "none").
  async onSaveLoaded({ detail: { file } }) {
    try {
      const { text, hexStart, hexLength, doc, originalSize } = await loadFile(file)
      const kind = detectSaveKind(doc)
      this.state = { file, text, hexStart, hexLength, doc, originalSize, kind }
      this.renderFieldTable()
      // Extras is the Fusion Mode patch path — show that panel, keep field browser collapsed.
      const isExtras = kind.id === "extras"
      if (isExtras) {
        this.renderFusionPanel()
        this.fusionPanelTarget.hidden = false
      } else {
        this.fusionPanelTarget.hidden = true
        this.fusionContentTarget.replaceChildren()
      }
      this.browserPanelTarget.hidden = false
      this.stats = kind.id === "slot" ? extractSlotStats(doc) : null
      if (this.stats) {
        // Slot saves get the report's own toolbar (Load another save, spoilers), like the other tools.
        this.saveLoaderOutlet.element.hidden = true
        this.fieldsDetailsTarget.open = this.ui.tabs.main === EDIT_TAB
        this.renderReport()
        this.reportTarget.scrollIntoView({ block: "start" })
      } else {
        this.saveLoaderOutlet.element.hidden = false
        this.saveLoaderOutlet.showFile(
          file.name,
          `Detected: ${kind.label} · ${doc.fields.length.toLocaleString()} fields · ` +
          `${originalSize.toLocaleString()} bytes decoded · magic 0x${doc.magic.toString(16)}`,
        )
        this.fieldsDetailsTarget.open = !isExtras
        this.hideReport()
      }
    } catch (err) {
      this.hideReport()
      this.saveLoaderOutlet.showError(err.message || String(err))
    }
  }

  // --- Snapshot tabs (slot saves) ---------------------------------------------
  // Tabs re-render the snapshot, but the field editor is built once: it's parked in editHome before
  // each render, then moved into the Edit tab's slot, so edits, open rows and the filter survive.

  renderReport() {
    this.rememberClosedAreas()
    this.editHomeTarget.appendChild(this.browserPanelTarget)
    this.reportTarget.innerHTML = renderReport(this.stats, this.ui, this.gameData)
    const editSlot = this.reportTarget.querySelector("[data-mpo-edit-slot]")
    editSlot?.appendChild(this.browserPanelTarget)
    this.browserPanelTarget.hidden = !editSlot
    this.reportTarget.hidden = false
  }

  /** Area lists render open; ones the player closed stay closed across re-renders. */
  rememberClosedAreas() {
    this.ui.closed ??= new Set()
    this.reportTarget.querySelectorAll("details[data-collapse]").forEach((details) => {
      const key = details.dataset.collapse
      if (details.open) this.ui.closed.delete(key)
      else this.ui.closed.add(key)
    })
  }

  hideReport() {
    this.editHomeTarget.appendChild(this.browserPanelTarget)
    this.reportTarget.hidden = true
    this.reportTarget.replaceChildren()
  }

  selectTab(id) {
    this.ui.tabs.main = id
    writeStorage(TAB_STORAGE, id)
    if (id === EDIT_TAB) this.fieldsDetailsTarget.open = true
    this.renderReport()
    document.getElementById(tabId("mpo", "main", id))?.focus()
  }

  onReportClick(event) {
    if (event.target.closest("[data-load-another]")) {
      this.saveLoaderOutlet.chooseAnother()
      return
    }
    // Opens or closes every level of the collection tree; renderReport() keeps that state on redraw.
    const sections = event.target.closest("[data-completion-sections]")
    if (sections) {
      const open = sections.dataset.completionSections === "open"
      for (const details of this.reportTarget.querySelectorAll(".game-tool__completion details")) details.open = open
      return
    }
    const tab = event.target.closest("[data-tab]")
    if (tab) this.selectTab(tab.dataset.tab)
  }

  onReportChange(event) {
    const el = event.target
    const { setting } = el.dataset
    let selector = `[data-setting="${setting}"]`
    if (setting === "spoilers") {
      this.ui.spoilers = el.checked
      writeStorage(SPOILERS_STORAGE, this.ui.spoilers ? "1" : "0")
    } else if (setting?.startsWith("descriptions-")) {
      const section = setting.slice("descriptions-".length)
      this.ui.descriptions[section] = el.checked
      writeStorage(descriptionsStorage(section), el.checked ? "1" : "0")
    } else if (setting === "filter") {
      const key = el.dataset.filter
      this.ui.filters[key] = el.value
      writeStorage(FILTER_STORAGE[key], el.value)
      selector = `[data-filter="${key}"][value="${el.value}"]`
    } else {
      return
    }
    this.renderReport()
    this.reportTarget.querySelector(selector)?.focus()
  }

  // Arrow keys, Home and End move between tabs (WAI-ARIA tabs pattern).
  onReportKeydown(event) {
    if (!event.target.matches("[role=tab]")) return
    const id = nextTabId(event, tabIds(this.stats), this.ui.tabs.main)
    if (id) this.selectTab(id)
  }

  renderFusionPanel() {
    // Exact name from Metroid Extras.NUT — do not substring-match "fusion"
    // (that also hits "Phazon Infusion", etc.).
    const matches = this.state.doc.fields.filter(
      (f) => f.type === 0 && /^fusion\s+mode$/i.test(f.name),
    )
    this.fusionContentTarget.replaceChildren()
    if (matches.length === 0) {
      const p = document.createElement("p")
      p.className = "game-tool__fusion-none"
      p.textContent =
        'No "Fusion Mode" field was found in this file — it may not be an Extras save, or your game version names it differently. You can still browse and edit every field below.'
      this.fusionContentTarget.appendChild(p)
      return
    }

    matches.forEach((f) => {
      const row = document.createElement("div")
      row.className = "game-tool__status-row"

      const chip = document.createElement("span")
      const isUnlocked = f.value !== 0
      chip.className = "game-tool__chip " + (isUnlocked ? "game-tool__chip--unlocked" : "game-tool__chip--locked")
      chip.textContent = isUnlocked ? "UNLOCKED" : "LOCKED"

      const label = document.createElement("span")
      label.className = "game-tool__field-name"
      label.textContent = f.name

      row.appendChild(chip)
      row.appendChild(label)
      this.fusionContentTarget.appendChild(row)

      const explain = document.createElement("p")
      explain.className = "game-tool__fusion-explain"
      explain.textContent = isUnlocked
        ? "Fusion Mode is already unlocked. If the menu still shows ??????, something else is wrong."
        : "Fusion Mode is locked. If you have beaten the game already, this is the bug."
      this.fusionContentTarget.appendChild(explain)

      const btnRow = document.createElement("div")
      btnRow.className = "game-tool__btn-row"
      const fixBtn = document.createElement("button")
      fixBtn.type = "button"
      fixBtn.className = "game-tool__btn game-tool__btn--primary"
      fixBtn.textContent = "Set Unlocked"
      fixBtn.disabled = isUnlocked
      fixBtn.addEventListener("click", () => {
        f.value = 1
        chip.className = "game-tool__chip game-tool__chip--unlocked"
        chip.textContent = "UNLOCKED"
        fixBtn.disabled = true
        explain.textContent =
          "Fusion Mode is unlocked! Scroll down and download the edited file."
        this.syncFieldTableValue(f)
      })
      btnRow.appendChild(fixBtn)
      this.fusionContentTarget.appendChild(btnRow)
    })
  }

  syncFieldTableValue(f) {
    const input = this.inputRegistry.get(f)
    if (input) {
      input.value = formatNumber(f.value)
      input.classList.add("is-dirty")
    }
  }

  typeBadge(type, len) {
    const span = document.createElement("span")
    span.className = "game-tool__type-badge"
    if (type === 0) span.textContent = "real"
    else if (type === 1) span.textContent = "string"
    else if (type === 2) span.textContent = `array·${len}`
    else if (type === 15) span.textContent = "uint64"
    else span.textContent = "type " + type
    return span
  }

  makeScalarInput(node) {
    const input = document.createElement("input")
    input.className = "game-tool__val-input"
    input.type = "text"
    input.inputMode = node.type === 1 ? "text" : "decimal"
    input.value = node.type === 1 ? node.value : formatNumber(node.value)
    input.addEventListener("change", () => {
      if (node.type === 1) {
        node.value = input.value
      } else if (node.type === 15) {
        try {
          node.value = BigInt(input.value.trim() || "0")
        } catch {
          input.value = formatNumber(node.value)
          return
        }
      } else {
        const n = parseFloat(input.value)
        if (Number.isNaN(n)) {
          input.value = formatNumber(node.value)
          return
        }
        node.value = n
      }
      input.classList.add("is-dirty")
    })
    if (node.type === 0) this.inputRegistry.set(node, input)
    return input
  }

  renderArrayBody(container, arr) {
    arr.forEach((item, i) => {
      const row = document.createElement("div")
      row.className = "game-tool__array-item-row"
      const idx = document.createElement("span")
      idx.className = "game-tool__array-item-idx"
      idx.textContent = "[" + i + "]"
      row.appendChild(idx)
      row.appendChild(this.typeBadge(item.type, item.type === 2 ? item.value.length : 0))
      if (item.type === 2) {
        const toggle = document.createElement("button")
        toggle.type = "button"
        toggle.className = "game-tool__text-button"
        toggle.textContent = "expand"
        const nested = document.createElement("div")
        nested.className = "game-tool__array-body"
        toggle.addEventListener("click", () => {
          if (!nested.dataset.built) {
            this.renderArrayBody(nested, item.value)
            nested.dataset.built = "1"
          }
          nested.classList.toggle("is-open")
          toggle.textContent = nested.classList.contains("is-open") ? "collapse" : "expand"
        })
        row.appendChild(toggle)
        container.appendChild(row)
        container.appendChild(nested)
      } else {
        row.appendChild(this.makeScalarInput(item))
        container.appendChild(row)
      }
    })
  }

  addExpandRow(frag, tr, tdVal, toggleLabel, buildContent) {
    const toggle = document.createElement("button")
    toggle.type = "button"
    toggle.className = "game-tool__text-button"
    toggle.textContent = toggleLabel(false)
    tdVal.appendChild(toggle)
    tr.appendChild(tdVal)
    frag.appendChild(tr)

    const expandTr = document.createElement("tr")
    expandTr.className = "game-tool__expand-row"
    expandTr.dataset.name = tr.dataset.name
    const expandTd = document.createElement("td")
    expandTd.colSpan = 3
    expandTr.appendChild(expandTd)
    frag.appendChild(expandTr)

    toggle.addEventListener("click", () => {
      if (!expandTd.dataset.built) {
        buildContent(expandTd)
        expandTd.dataset.built = "1"
      }
      const opening = !expandTr.classList.contains("is-open")
      expandTr.classList.toggle("is-open", opening)
      toggle.textContent = toggleLabel(opening)
    })
  }

  renderFieldTable() {
    this.fieldsRootTarget.replaceChildren()
    this.inputRegistry.clear()
    this.fieldTotalTarget.textContent = `(${this.state.doc.fields.length.toLocaleString()})`

    if (usesFieldGroups(this.state.kind?.id)) {
      this.renderGroupedFields(groupSlotFields(this.state.doc.fields))
    } else {
      this.renderFlatFields(this.state.doc.fields)
    }
    this.applyFilter()
  }

  renderFlatFields(fields) {
    const scroll = document.createElement("div")
    scroll.className = "game-tool__table-scroll"
    scroll.appendChild(this.buildFieldsTable(fields))
    this.fieldsRootTarget.appendChild(scroll)
  }

  renderGroupedFields(groups) {
    groups.forEach((group) => {
      const details = document.createElement("details")
      details.className = "game-tool__field-group"
      details.open = Boolean(group.open)
      details.dataset.groupId = group.id

      const summary = document.createElement("summary")
      summary.className = "game-tool__field-group-summary"
      summary.innerHTML =
        `${esc(group.label)} ` +
        `<span class="game-tool__panel-count">(${group.fields.length.toLocaleString()})</span>`
      details.appendChild(summary)

      const scroll = document.createElement("div")
      scroll.className = "game-tool__table-scroll game-tool__table-scroll--grouped"
      scroll.appendChild(this.buildFieldsTable(group.fields))
      details.appendChild(scroll)

      this.fieldsRootTarget.appendChild(details)
    })
  }

  buildFieldsTable(fields) {
    const table = document.createElement("table")
    table.className = "game-tool__fields"
    const thead = document.createElement("thead")
    thead.innerHTML = "<tr><th scope=\"col\">Name</th><th scope=\"col\">Type</th><th scope=\"col\">Value</th></tr>"
    table.appendChild(thead)

    const tbody = document.createElement("tbody")
    const frag = document.createDocumentFragment()
    fields.forEach((f) => this.appendFieldRows(frag, f))
    tbody.appendChild(frag)
    table.appendChild(tbody)
    return table
  }

  appendFieldRows(frag, f) {
    const tr = document.createElement("tr")
    tr.dataset.name = f.name.toLowerCase()

    const tdName = document.createElement("td")
    tdName.textContent = f.name
    tr.appendChild(tdName)

    const tdType = document.createElement("td")
    tdType.appendChild(this.typeBadge(f.type, f.type === 2 ? f.value.length : 0))
    tr.appendChild(tdType)

    const tdVal = document.createElement("td")
    if (f.type === 2) {
      const count = f.value.length
      const label = (open) => `${open ? "collapse" : "expand"} ${count} item${count === 1 ? "" : "s"}`
      this.addExpandRow(frag, tr, tdVal, label, (container) => this.renderArrayBody(container, f.value))
    } else if (f.type === 1 && f.value.length > 120) {
      const label = (open) => `${open ? "collapse" : "expand"} · ${f.value.length} chars`
      this.addExpandRow(frag, tr, tdVal, label, (container) => {
        const ta = document.createElement("textarea")
        ta.className = "game-tool__val-input game-tool__expand-textarea"
        ta.rows = 8
        ta.value = f.value
        ta.addEventListener("change", () => {
          f.value = ta.value
          ta.classList.add("is-dirty")
        })
        container.appendChild(ta)
      })
    } else {
      tdVal.appendChild(this.makeScalarInput(f))
      tr.appendChild(tdVal)
      frag.appendChild(tr)
    }
  }

  applyFilter() {
    const q = this.searchInputTarget.value.trim().toLowerCase()
    let shown = 0

    this.fieldsRootTarget.querySelectorAll("tbody tr").forEach((tr) => {
      const match = !q || (tr.dataset.name || "").includes(q)
      tr.hidden = !match
      if (match && !tr.classList.contains("game-tool__expand-row")) shown++
    })

    this.fieldsRootTarget.querySelectorAll(".game-tool__field-group").forEach((group) => {
      const anyVisible = Array.from(group.querySelectorAll("tbody tr")).some(
        (tr) => !tr.hidden && !tr.classList.contains("game-tool__expand-row"),
      )
      group.hidden = q ? !anyVisible : false
      if (q && anyVisible) group.open = true
    })

    this.searchCountTarget.textContent = q
      ? `${shown.toLocaleString()} match${shown === 1 ? "" : "es"}`
      : ""
  }

  download(filename, content, mime) {
    const blob = new Blob([content], { type: mime })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    setTimeout(() => URL.revokeObjectURL(url), 2000)
  }

  downloadNut() {
    if (!this.state) return
    const newText = rebuildText(this.state.text, this.state.hexStart, this.state.hexLength, this.state.doc)
    this.download(this.state.file.name, newText, "text/plain")
  }

  downloadJson() {
    if (!this.state) return
    const json = JSON.stringify(this.state.doc, (_k, v) => (typeof v === "bigint" ? v.toString() : v), 2)
    this.download(this.state.file.name.replace(/\.NUT$/i, "") + ".json", json, "application/json")
  }
}